// Service Payment gateway utilities - extends existing payment utilities for external services

import { ServiceType } from 'types/services';
import { 
  CCAvenueGatewayData, 
  PaymentInitiationResponse, 
  validatePaymentData as validateCoursePaymentData,
  parsePaymentCallback,
  getPaymentStatus,
  formatAmountForCCAvenue 
} from './paymentUtils';

export interface ServiceGatewayData extends Omit<CCAvenueGatewayData, 'merchant_param1' | 'merchant_param2'> {
  merchant_param1: string; // service_type:service_id format
  merchant_param2: string; // payment_type:service
  merchant_param3?: string; // additional service metadata
}

export interface ServicePaymentInitiationResponse extends PaymentInitiationResponse {
  service_purchase?: {
    id: string;
    service_id: string;
    service_type: ServiceType;
    transaction_id?: string;
    total_amount: number;
    currency: string;
    expires_at?: string;
  };
}

/**
 * Validates service payment form data before submission
 * Extends the base payment validation with service-specific checks
 * @param gatewayData - Service payment gateway parameters
 * @returns boolean indicating if data is valid
 */
export const validateServicePaymentData = (gatewayData: ServiceGatewayData): boolean => {
  // First run base validation
  if (!validateCoursePaymentData(gatewayData)) {
    return false;
  }

  // Service-specific validations
  if (!gatewayData.merchant_param1) {
    console.error('Missing service identifier in merchant_param1');
    return false;
  }

  // Validate service identifier format (service_type:service_id)
  const serviceIdentifier = gatewayData.merchant_param1;
  const serviceIdPattern = /^[a-zA-Z_]+:[a-zA-Z0-9-_]+$/;
  if (!serviceIdPattern.test(serviceIdentifier)) {
    console.error('Invalid service identifier format in merchant_param1. Expected: service_type:service_id');
    return false;
  }

  // Validate payment type
  if (!gatewayData.merchant_param2 || !gatewayData.merchant_param2.includes('service')) {
    console.error('Invalid payment type in merchant_param2. Expected to contain "service"');
    return false;
  }

  return true;
};

/**
 * Parses service payment callback response from URL parameters
 * Extends the base callback parser with service-specific data extraction
 * @param searchParams - URL search parameters from callback
 * @returns parsed service payment response
 */
export const parseServicePaymentCallback = (searchParams: URLSearchParams) => {
  const baseResponse = parsePaymentCallback(searchParams);
  
  // Extract service-specific information from merchant params
  const serviceInfo = extractServiceInfoFromMerchantParams({
    merchantParam1: baseResponse.merchantParam1,
    merchantParam2: baseResponse.merchantParam2,
    merchantParam3: baseResponse.merchantParam3
  });

  return {
    ...baseResponse,
    serviceType: serviceInfo.serviceType,
    serviceId: serviceInfo.serviceId,
    paymentType: serviceInfo.paymentType,
    serviceMetadata: serviceInfo.metadata
  };
};

/**
 * Extracts service information from merchant parameters
 * @param merchantParams - Merchant parameters from payment response
 * @returns service information object
 */
export const extractServiceInfoFromMerchantParams = (merchantParams: {
  merchantParam1?: string | null;
  merchantParam2?: string | null;
  merchantParam3?: string | null;
}): {
  serviceType?: ServiceType;
  serviceId?: string;
  paymentType?: string;
  metadata?: any;
} => {
  try {
    const result: any = {};

    // Extract service_type:service_id from merchant_param1
    if (merchantParams.merchantParam1) {
      const [serviceType, serviceId] = merchantParams.merchantParam1.split(':');
      result.serviceType = serviceType as ServiceType;
      result.serviceId = serviceId;
    }

    // Extract payment type from merchant_param2
    if (merchantParams.merchantParam2) {
      const [paymentType] = merchantParams.merchantParam2.split(':');
      result.paymentType = paymentType;
    }

    // Parse additional metadata from merchant_param3 if present
    if (merchantParams.merchantParam3) {
      try {
        result.metadata = JSON.parse(merchantParams.merchantParam3);
      } catch {
        // If not JSON, just store as string
        result.metadata = merchantParams.merchantParam3;
      }
    }

    return result;
  } catch (error) {
    console.error('Error extracting service info from merchant params:', error);
    return {};
  }
};

/**
 * Generates redirect URLs for service payments
 * All URLs point to the service payment callback system
 * @param serviceType - Type of service (selfcare, etc.)
 * @param serviceId - Service identifier
 * @param baseUrl - Base application URL
 * @returns object with success, error, and cancel URLs
 */
export const generateServiceRedirectUrls = (
  serviceType: ServiceType, 
  serviceId: string, 
  baseUrl: string = window.location.origin
) => {
  const basePath = `${baseUrl}/buy-services/${serviceType}/${serviceId}`;
  return {
    success: `${basePath}/transaction_completed`,
    error: `${basePath}/transaction_completed`,
    cancel: `${basePath}/transaction_completed`
  };
};

/**
 * Formats service amount for CCAvenue
 * Handles service-specific amount formatting if needed
 * @param amount - Amount in decimal format
 * @param serviceType - Type of service
 * @returns formatted amount string
 */
export const formatServiceAmountForCCAvenue = (
  amount: number, 
  serviceType?: ServiceType
): string => {
  // For now, use the same formatting as courses
  // Can be extended for service-specific formatting if needed
  return formatAmountForCCAvenue(amount);
};

/**
 * Creates merchant parameters for service payments
 * @param serviceType - Type of service
 * @param serviceId - Service identifier
 * @param additionalData - Any additional service-specific data
 * @returns merchant parameters object
 */
export const createServiceMerchantParams = (
  serviceType: ServiceType,
  serviceId: string,
  additionalData?: any
) => {
  return {
    merchant_param1: `${serviceType}:${serviceId}`,
    merchant_param2: `payment_type:service`,
    merchant_param3: additionalData ? JSON.stringify(additionalData) : undefined
  };
};

/**
 * Validates service booking/purchase expiry
 * @param expiresAt - ISO date string of expiry
 * @returns boolean indicating if still valid
 */
export const isServicePurchaseValid = (expiresAt?: string): boolean => {
  if (!expiresAt) {
    // No expiry means it's always valid
    return true;
  }

  const expiryDate = new Date(expiresAt);
  const now = new Date();
  
  return now < expiryDate;
};

/**
 * Calculates service purchase expiry date
 * @param validityDays - Number of days the service is valid for
 * @param purchaseDate - Date of purchase (defaults to now)
 * @returns ISO date string of expiry
 */
export const calculateServiceExpiry = (
  validityDays?: number,
  purchaseDate: Date = new Date()
): string => {
  if (!validityDays) {
    // Default to 30 days if not specified
    validityDays = 30;
  }

  const expiryDate = new Date(purchaseDate);
  expiryDate.setDate(expiryDate.getDate() + validityDays);
  
  return expiryDate.toISOString();
};

/**
 * Gets service-specific payment status message
 * @param paymentStatus - Payment status from gateway
 * @param serviceType - Type of service
 * @returns user-friendly status message
 */
export const getServicePaymentStatusMessage = (
  paymentStatus: 'success' | 'failed' | 'cancelled' | 'pending',
  serviceType: ServiceType
): string => {
  const serviceNames = {
    selfcare: 'Self-Care Session',
    counselling: 'Counselling Session', 
    wellness: 'Wellness Service',
    coaching: 'Coaching Session'
  };

  const serviceName = serviceNames[serviceType] || 'Service';

  switch (paymentStatus) {
    case 'success':
      return `${serviceName} purchase successful! You can now schedule your session.`;
    case 'failed':
      return `${serviceName} purchase failed. Please try again or contact support.`;
    case 'cancelled':
      return `${serviceName} purchase was cancelled. You can try again anytime.`;
    case 'pending':
      return `${serviceName} purchase is being processed. Please wait...`;
    default:
      return `${serviceName} purchase status unknown. Please contact support.`;
  }
};

/**
 * Extracts service ID from various URL patterns
 * @param url - The URL to extract from
 * @returns Service type and ID or null
 */
export const extractServiceInfoFromUrl = (url: string): {
  serviceType?: ServiceType;
  serviceId?: string;
} | null => {
  // Handle patterns like /buy-services/selfcare/one-on-one-counselling
  const serviceMatch = url.match(/\/buy-services\/([^\/]+)\/([^\/]+)/);
  if (serviceMatch) {
    return {
      serviceType: serviceMatch[1] as ServiceType,
      serviceId: serviceMatch[2]
    };
  }

  // Handle URL parameters
  const urlParams = new URLSearchParams(url.split('?')[1] || '');
  const serviceType = urlParams.get('service_type');
  const serviceId = urlParams.get('service_id');
  
  if (serviceType && serviceId) {
    return {
      serviceType: serviceType as ServiceType,
      serviceId: serviceId
    };
  }

  return null;
};

// Helper functions for generic payment result pages
import { PaymentResultProps, NextStepItem, FAQItem } from '../app/pages/PaymentFlows/Generic/PaymentResult/types';
import { createPaymentResultProps } from '../app/pages/PaymentFlows/Generic/PaymentResult/paymentResultConfig';
import { colors } from '../styles/colors';

/**
 * Service type mapping for different payment flows
 */
export type ServiceFlowType = 'selfcare' | 'mes' | 'course' | 'default';

// Service-specific Next Steps content
const NEXT_STEPS = {
  selfcare: [
    {
      step: 1,
      title: 'Check your email for booking instructions and session details',
      description: '',
      color: colors.successGreen
    },
    {
      step: 2,
      title: 'Schedule your counselling session at your preferred time',
      description: '',
      color: colors.primaryBlue
    }
  ] as NextStepItem[],
  mes: [
    {
      step: 1,
      title: 'Go to the Dashboard / Ma Foi Score tab and attempt the assessment by clicking on the Take Test button.',
      description: '',
      color: colors.successGreen
    },
  ] as NextStepItem[],
  course: [
    {
      step: 1,
      title: 'Access your course from the student dashboard',
      description: '',
      color: colors.successGreen
    },
    {
      step: 2,
      title: 'Start learning with the course materials and videos',
      description: '',
      color: colors.primaryBlue
    }
  ] as NextStepItem[],
  
};

// Service-specific FAQ content
const FAQ_DATA = {
  selfcare: [
    {
      id: 'panel1',
      question: "Should I see a therapist?",
      answer: "Absolutely, therapy isn't just for crisis moments. Therapy isn't only for those with mental health diagnoses; it's a proactive, evidence-based way to boost emotional well-being, resilience, and personal growth."
    },
    {
      id: 'panel2', 
      question: "How often are sessions for therapy?",
      answer: "Therapy is often weekly or bi-weekly, but it really depends on you. Your therapist will help find a pace that fits your goals and progress."
    },
    {
      id: 'panel3',
      question: "Is everything I say in therapy confidential?",
      answer: "Yes. Your privacy is fully respected and your records will not be shared with anybody including your institution and Prosculpt. Therapists only break confidentiality if there's a risk of harm to you or someone else."
    },
    {
      id: 'panel4',
      question: "Can a therapist prescribe medication?",
      answer: "No, therapists cannot prescribe medication. Only psychiatrists, who are medical doctors have the authority to prescribe medication."
    },
    {
      id: 'panel5',
      question: "Is medication a substitute for therapy?",
      answer: "They serve different purposes. Therapy builds lasting emotional skills; medication can help with symptom relief. In some cases, a combination of both is most effective."
    }
  ] as FAQItem[],
  mes: [
    {
      id: 'panel1',
      question: "I didn't receive my payment confirmation. What should I do?",
      answer: `Please check your email's spam or junk folder. If you still haven't received the confirmation, contact our support team at <a href="mailto:info@prosculpt.co">info@prosculpt.co</a>`
    },
    {
      id: 'panel2', 
      question: "Can I get a refund if I don't like the MES Assessment?",
      answer: `Currently, we do not offer refunds once an assessment is purchased. Please review the MES Assessment details carefully before buying. However, you can write to us at <a href="mailto:info@prosculpt.co">info@prosculpt.co</a> to share what you didn't like or suggest improvements that could enhance the assessment experience.`
    },
    {
      id: 'panel3',
      question: "Can I get a refund if I purchased the MES Assessment independently, and later my institute also purchased it?",
      answer: "Currently, we do not offer refunds for assessments purchased independently, even if your institute later purchases the same MES Assessment. However, we will notify your institute about your individual purchase for their reference. We recommend reviewing the MES Assessment details carefully before making a purchase."
    },
    {
      id: 'panel4',
      question: "Will I be able to attempt the MES Assessment immediately after purchase?",
      answer: "Yes. Once the payment is successfully processed, you will get immediate access to attempt the MES Assessment."
    },
    {
      id: 'panel5',
      question: "How will I attempt the MES Assessment after purchase?",
      answer: "After a successful payment, the Ma Foi Employability Score section will be added to the dashboard screen, and a Ma Foi Score tab will also appear in the top navigation bar. From there, you can access and attempt the assessment."
    },
    {
      id: 'panel6',
      question: "What happens if my payment fails?",
      answer: "If your payment fails, you will see a message saying: “Payment Failed! Something went wrong. Try the payment again.” You will be redirected back to the Checkout Page to retry."
    },
    {
      id: 'panel7',
      question: "Will I receive a receipt or invoice after payment?",
      answer: "Yes. A receipt will be emailed to you immediately after a successful transaction."
    },
    {
      id: 'panel8',
      question: "What payment methods are accepted?",
      answer: "We accept UPI, Credit/Debit Cards, Net Banking, and most major wallets through our secure Payment Gateway."
    },
  ] as FAQItem[],
  course: [
    {
      id:'panel1',
      question: "How do I access my purchased course?",
      answer: "You can access your course from the 'My Courses' section in your student dashboard. The course will be immediately available after purchase.",
      defaultExpanded: true
    },
    {
      id:'panel2',
      question: "Can I download course materials for offline viewing?",
      answer: "Course materials availability for download depends on the specific course. Check the course details page for download options.",
      defaultExpanded: false
    },
    {
      id:'panel3',
      question: "Is there a completion certificate?",
      answer: "Yes, you'll receive a certificate of completion once you finish all course modules and assessments (if applicable).",
      defaultExpanded: false
    },
    {
      id:'panel4',
      question: "How long do I have access to the course?",
      answer: "Most courses offer lifetime access. Check your specific course details for any access limitations.",
      defaultExpanded: false
    },
    {
      id:'panel5',
      question: "Can I get a refund for the course?",
      answer: `Please refer to our refund policy or contact support at <a href="mailto:info@prosculpt.co">info@prosculpt.co</a> within 7 days of purchase.`,
      defaultExpanded: false
    }
  ] as FAQItem[],
  
};

/**
 * Extract service type from URL pathname
 * @param pathname - The current URL pathname
 * @returns ServiceFlowType
 */
export const getServiceTypeFromPath = (pathname: string): ServiceFlowType => {
  console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - Input pathname:', pathname);
  
  let result: ServiceFlowType = 'default';
  
  if (pathname.includes('/student-selfcare/')) {
    result = 'selfcare';
    console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - Matched SELFCARE pattern');
  } else if (pathname.includes('/student-mes-purchase-details/')) {
    result = 'mes';
    console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - Matched MES pattern');
  } else if (pathname.includes('/course/') || pathname.includes('/buy-course/')) {
    result = 'course';
    console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - Matched COURSE pattern');
  } else {
    console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - NO PATTERN MATCHED, using default');
  }
  
  console.log('[PAYMENT_DEBUG] getServiceTypeFromPath - Final result:', result);
  return result;
};

/**
 * Get service-specific configuration based on service type
 * @param serviceType - The service type
 * @returns Service configuration object
 */
export const getServiceConfiguration = (serviceType: ServiceFlowType) => {
  const configs = {
    selfcare: {
      serviceName: 'One-on-One Counselling',
      providerName: 'Clovemind',
      serviceIdentifier: 'clovemind',
      retryPath: '/student-selfcare-details',
      dashboardPath: '/student-selfcare',
      // Status-specific configurations
      successButtonText: 'Go to Dashboard',
      successButtonIcon: 'selfcare_icon',
      failedButtonText: 'Go to Dashboard',
      failedButtonIcon: 'selfcare_icon',
      cancelledButtonText: 'Go to Dashboard',
      cancelledButtonIcon: 'selfcare_icon',
      showNextSteps: true,
      showFAQ: false, // success only
      nextStepsContent: NEXT_STEPS.selfcare,
      faqData: FAQ_DATA.selfcare
    },
    mes: {
      serviceName: 'Ma Foi Employability Score Assessment',
      providerName: 'Jombay',
      serviceIdentifier: 'jombay',
      retryPath: '/student-mes-purchase-details',
      dashboardPath: '/student-dashboard',
      // Status-specific configurations
      successDashboardPath: '/student-mafoi-employability-score',
      successButtonText: 'Go To Ma Foi Score',
      successButtonIcon: 'score_icon',
      failedDashboardPath: '/student-dashboard',
      failedButtonText: 'Go to Dashboard',
      failedButtonIcon: 'home_icon',
      cancelledDashboardPath: '/student-dashboard',
      cancelledButtonText: 'Go to Dashboard',
      cancelledButtonIcon: 'home_icon',
      showNextSteps: true,
      showFAQ: false, // success only
      nextStepsContent: NEXT_STEPS.mes,
      faqData: FAQ_DATA.mes
    },
    course: {
      serviceName: 'Course',
      providerName: 'ProSculpt',
      serviceIdentifier: undefined,
      retryPath: '/courses',
      dashboardPath: '/student-dashboard',
      showNextSteps: true,
      showFAQ: false,
      nextStepsContent: NEXT_STEPS.course,
      faqData: FAQ_DATA.course
    },
    default: {
      serviceName: 'Service',
      providerName: 'Provider',
      serviceIdentifier: undefined,
      retryPath: '/dashboard',
      dashboardPath: '/dashboard',
      showNextSteps: false,
      showFAQ: false,
      nextStepsContent: [],
      faqData: []
    }
  };

  return configs[serviceType] || configs.default;
};

/**
 * Creates dynamic payment result props based on service type and status
 * @param pathname - Current URL pathname to extract service type
 * @param status - Payment status ('success' | 'failure' | 'cancelled')
 * @param customProps - Additional custom props
 * @returns PaymentResultProps
 */
export const createDynamicPaymentProps = (
  pathname: string,
  status: 'success' | 'failure' | 'cancelled',
  customProps?: Partial<PaymentResultProps>
): PaymentResultProps => {
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Input pathname:', pathname);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Input status:', status);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Custom props:', customProps);
  
  const serviceType = getServiceTypeFromPath(pathname);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Detected serviceType:', serviceType);
  
  const config = getServiceConfiguration(serviceType);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Service configuration:', config);
  
  // Determine preset key based on service type and status
  let presetKey: keyof typeof import('../app/pages/PaymentFlows/Generic/PaymentResult/paymentResultConfig').PAYMENT_RESULT_PRESETS;
  
  if (serviceType === 'course') {
    presetKey = `course${status.charAt(0).toUpperCase() + status.slice(1)}` as any;
    console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Using COURSE preset:', presetKey);
  } else {
    presetKey = `selfcare${status.charAt(0).toUpperCase() + status.slice(1)}` as any;
    console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Using SELFCARE preset (default):', presetKey);
  }

  const finalProps = {
    serviceName: config.serviceName,
    providerName: config.providerName,
    serviceIdentifier: config.serviceIdentifier,
    retryPath: config.retryPath,
    // Use status-specific path if available, fallback to default
    dashboardPath: status === 'success' && (config as any).successDashboardPath
      ? (config as any).successDashboardPath
      : status === 'failure' && (config as any).failedDashboardPath
      ? (config as any).failedDashboardPath
      : status === 'cancelled' && (config as any).cancelledDashboardPath
      ? (config as any).cancelledDashboardPath
      : config.dashboardPath,
    // Pass status-specific button configuration
    buttonText: status === 'success' && (config as any).successButtonText
      ? (config as any).successButtonText
      : status === 'failure' && (config as any).failedButtonText
      ? (config as any).failedButtonText
      : status === 'cancelled' && (config as any).cancelledButtonText
      ? (config as any).cancelledButtonText
      : undefined,
    buttonIcon: status === 'success' && (config as any).successButtonIcon
      ? (config as any).successButtonIcon
      : status === 'failure' && (config as any).failedButtonIcon
      ? (config as any).failedButtonIcon
      : status === 'cancelled' && (config as any).cancelledButtonIcon
      ? (config as any).cancelledButtonIcon
      : undefined,
    showNextSteps: status === 'success' ? config.showNextSteps : false,
    showFAQ: status === 'failure' ? true : config.showFAQ,
    // Pass service-specific content
    nextStepsContent: status === 'success' && (config as any).nextStepsContent ? (config as any).nextStepsContent : undefined,
    faqData: status === 'failure' && (config as any).faqData ? (config as any).faqData : undefined,
    ...customProps
  };
  
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Final props before createPaymentResultProps:', finalProps);
  
  const result = createPaymentResultProps(presetKey, finalProps);
  
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Final result from createPaymentResultProps:', result);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Result paymentStatus:', result.paymentStatus);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Result serviceName:', result.serviceName);
  console.log('[PAYMENT_DEBUG] createDynamicPaymentProps - Result providerName:', result.providerName);
  
  return result;
};

/**
 * Creates props for service payment success page
 * @param serviceName - Name of the service
 * @param providerName - Name of the service provider
 * @param serviceIdentifier - Service identifier for API calls
 * @param customProps - Additional custom props
 * @returns PaymentResultProps for success page
 */
export const createServiceSuccessProps = (
  serviceName?: string,
  providerName?: string,
  serviceIdentifier?: string,
  customProps?: Partial<PaymentResultProps>
): PaymentResultProps => {
  return createPaymentResultProps('selfcareSuccess', {
    serviceName,
    providerName,
    serviceIdentifier,
    retryPath: '/student-selfcare-details',
    dashboardPath: '/student-selfcare',
    ...customProps
  });
};

/**
 * Creates props for service payment failure page
 * @param serviceName - Name of the service
 * @param providerName - Name of the service provider
 * @param serviceIdentifier - Service identifier for API calls
 * @param customProps - Additional custom props
 * @returns PaymentResultProps for failure page
 */
export const createServiceFailureProps = (
  serviceName?: string,
  providerName?: string,
  serviceIdentifier?: string,
  customProps?: Partial<PaymentResultProps>
): PaymentResultProps => {
  return createPaymentResultProps('selfcareFailure', {
    serviceName,
    providerName,
    serviceIdentifier,
    retryPath: '/student-selfcare-details',
    dashboardPath: '/student-selfcare',
    ...customProps
  });
};

/**
 * Creates props for service payment cancelled page
 * @param serviceName - Name of the service
 * @param providerName - Name of the service provider
 * @param serviceIdentifier - Service identifier for API calls
 * @param customProps - Additional custom props
 * @returns PaymentResultProps for cancelled page
 */
export const createServiceCancelledProps = (
  serviceName?: string,
  providerName?: string,
  serviceIdentifier?: string,
  customProps?: Partial<PaymentResultProps>
): PaymentResultProps => {
  return createPaymentResultProps('selfcareCancelled', {
    serviceName,
    providerName,
    serviceIdentifier,
    retryPath: '/student-selfcare-details',
    dashboardPath: '/student-selfcare',
    ...customProps
  });
};

/**
 * Creates props for course payment pages (for future use)
 * @param status - Payment status
 * @param courseName - Name of the course
 * @param customProps - Additional custom props
 * @returns PaymentResultProps for course payment pages
 */
export const createCoursePaymentProps = (
  status: 'success' | 'failure' | 'cancelled',
  courseName?: string,
  customProps?: Partial<PaymentResultProps>
): PaymentResultProps => {
  const presetKey = `course${status.charAt(0).toUpperCase() + status.slice(1)}` as keyof typeof import('../app/pages/PaymentFlows/Generic/PaymentResult/paymentResultConfig').PAYMENT_RESULT_PRESETS;
  
  return createPaymentResultProps(presetKey, {
    serviceName: courseName,
    serviceIdentifier: undefined, // Don't fetch service provider for courses
    retryPath: '/courses', // This would need to be dynamic based on course
    dashboardPath: '/student-dashboard',
    ...customProps
  });
};